'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, Button, Input } from '@ui';
import { IconCalendar, IconChevronLeft, IconChevronRight } from '@icons';

type DateInputPopoverProps = {
  id?: string;
  name?: string;
  label?: React.ReactNode;
  value?: string; // YYYY-MM-DD controlled
  onChange?: (isoDate: string | '') => void;
  className?: string;
};

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const daysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
const monthLabel = (date: Date) =>
  date.toLocaleString(undefined, { month: 'long', year: 'numeric' });

export default function DateInputPopover({
  id,
  name,
  label = 'Start date',
  value,
  onChange,
  className = '',
}: DateInputPopoverProps) {
  const fieldId = id ?? 'date-input';
  const inputId = `${fieldId}-input`;
  const popoverId = `${fieldId}-popover`;
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() => new Date());
  const [selectedIso, setSelectedIso] = useState<string | ''>(value ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [popStyle, setPopStyle] = useState<React.CSSProperties>({
    position: 'absolute',
    zIndex: 9999,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  interface Cell {
    date: Date;
    out: boolean;
  }

  const weeks = useMemo(() => {
    const start = startOfMonth(viewDate);
    const firstWeekday = start.getDay();
    const total = daysInMonth(viewDate);
    const startDate = new Date(start.getFullYear(), start.getMonth(), 1 - firstWeekday);
    const cellsCount = Math.ceil((firstWeekday + total) / 7) * 7;
    const grid: Cell[][] = [];
    let week: Cell[] = [];
    for (let i = 0; i < cellsCount; i++) {
      const d = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i);
      const out = d.getMonth() !== viewDate.getMonth();
      week.push({ date: d, out });
      if (week.length === 7) {
        grid.push(week);
        week = [];
      }
    }
    if (week.length) grid.push(week);
    return grid;
  }, [viewDate]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!open) return;
      if (!popRef.current) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (popRef.current.contains(target)) return;
      if (inputRef.current && inputRef.current.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    const updatePosition = () => {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      const top = rect.bottom + scrollY + 8;
      let left = rect.left + scrollX;
      const minWidth = rect.width;
      const maxRight = scrollX + window.innerWidth - 8;
      if (left + minWidth > maxRight) left = Math.max(scrollX + 8, maxRight - minWidth);
      setPopStyle((s) => ({ ...s, top, left, minWidth }));
    };
    if (open) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, { passive: true });
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [open, viewDate]);

  const selectDate = (date: Date) => {
    const iso = toISO(date);
    setSelectedIso(iso);
    onChange?.(iso);
    setOpen(false);
  };

  // keyboard navigation state
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const flatCells = useMemo(() => {
    const arr: { date: Date; out: boolean }[] = [];
    weeks.forEach((wk) => wk.forEach((c) => arr.push(c)));
    return arr;
  }, [weeks]);

  useEffect(() => {
    if (!open) return;
    const selectedIdx = flatCells.findIndex((c) => toISO(c.date) === selectedIso);
    if (selectedIdx >= 0) {
      setActiveIndex(selectedIdx);
      return;
    }
    const todayIdx = flatCells.findIndex((c) => toISO(c.date) === toISO(new Date()));
    setActiveIndex(todayIdx >= 0 ? todayIdx : 0);
  }, [open, flatCells, selectedIso]);

  useEffect(() => {
    if (activeIndex == null) return;
    const node = popRef.current?.querySelector(
      `[data-index='${activeIndex}']`
    ) as HTMLElement | null;
    if (node) node.focus();
  }, [activeIndex]);

  const clampIndex = (i: number) => Math.max(0, Math.min(flatCells.length - 1, i));

  const onGridKeyDown = (e: React.KeyboardEvent) => {
    if (activeIndex == null) return;
    const rowLen = 7;
    let next = activeIndex;
    switch (e.key) {
      case 'ArrowRight':
        next = clampIndex(activeIndex + 1);
        break;
      case 'ArrowLeft':
        next = clampIndex(activeIndex - 1);
        break;
      case 'ArrowDown':
        next = clampIndex(activeIndex + rowLen);
        break;
      case 'ArrowUp':
        next = clampIndex(activeIndex - rowLen);
        break;
      case 'Home':
        next = activeIndex - (activeIndex % rowLen);
        break;
      case 'End':
        next = activeIndex - (activeIndex % rowLen) + (rowLen - 1);
        break;
      case 'PageUp':
        setViewDate((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1));
        next = clampIndex(activeIndex - rowLen * 4);
        break;
      case 'PageDown':
        setViewDate((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1));
        next = clampIndex(activeIndex + rowLen * 4);
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        const cell = flatCells[activeIndex];
        if (cell) selectDate(cell.date);
        return;
      }
      case 'Escape':
        setOpen(false);
        return;
      default:
        return;
    }
    e.preventDefault();
    setActiveIndex(next);
  };

  const clear = () => {
    setSelectedIso('');
    onChange?.('');
  };
  const goToday = () => {
    const today = new Date();
    setViewDate(today);
    const iso = toISO(today);
    setSelectedIso(iso);
    onChange?.(iso);
    setOpen(false);
  };
  const prevMonth = () => setViewDate((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1));
  const nextMonth = () => setViewDate((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1));

  return (
    <div className={`date-input-component ${className}`.trim()} id={id}>
      <div className='date-input' data-dateinput>
        <fieldset>
          <label htmlFor={inputId}>{label}</label>
          <div className='input-affix input-date'>
            <Input
              ref={inputRef}
              id={inputId}
              name={name}
              placeholder='YYYY-MM-DD'
              readOnly
              value={selectedIso}
              onFocus={() => setOpen(true)}
            />
            <button
              type='button'
              className='affix-icon affix-icon-button'
              aria-label='Open calendar'
              onClick={() => setOpen((o) => !o)}
            >
              <IconCalendar />
            </button>
          </div>
        </fieldset>

        {open && typeof document !== 'undefined'
          ? createPortal(
              <Card
                className='date-popover'
                id={popoverId}
                role='dialog'
                aria-modal='false'
                ref={popRef}
                style={popStyle}
              >
                <div className='date-picker'>
                  <div className='date-picker-nav'>
                    <Button
                      label={undefined}
                      icon={<IconChevronLeft />}
                      className='btn-ghost btn-sm'
                      data-prev
                      onClick={prevMonth}
                    />
                    <b className='date-picker-title'>{monthLabel(viewDate)}</b>
                    <Button
                      label={undefined}
                      icon={<IconChevronRight />}
                      className='btn-ghost btn-sm'
                      data-next
                      onClick={nextMonth}
                    />
                  </div>
                  <div
                    className='date-picker-grid'
                    role='grid'
                    aria-label={monthLabel(viewDate)}
                    onKeyDown={onGridKeyDown}
                    tabIndex={0}
                  >
                    <div className='date-picker-row' role='row'>
                      <div className='date-picker-cell date-picker-head' role='columnheader'>
                        Su
                      </div>
                      <div className='date-picker-cell date-picker-head' role='columnheader'>
                        Mo
                      </div>
                      <div className='date-picker-cell date-picker-head' role='columnheader'>
                        Tu
                      </div>
                      <div className='date-picker-cell date-picker-head' role='columnheader'>
                        We
                      </div>
                      <div className='date-picker-cell date-picker-head' role='columnheader'>
                        Th
                      </div>
                      <div className='date-picker-cell date-picker-head' role='columnheader'>
                        Fr
                      </div>
                      <div className='date-picker-cell date-picker-head' role='columnheader'>
                        Sa
                      </div>
                    </div>

                    {weeks.map((wk, i) => (
                      <div className='date-picker-row' role='row' key={i}>
                        {wk.map((cell, j) => {
                          const iso = toISO(cell.date);
                          const isSelected = iso === selectedIso;
                          const todayIso = toISO(new Date());
                          const isToday = iso === todayIso;
                          const idx = i * 7 + j;
                          return (
                            <div
                              className={`date-picker-cell ${cell.out ? 'date-picker-out' : ''} ${isSelected ? 'date-picker-selected' : ''} ${isToday ? 'date-picker-today' : ''}`}
                              key={`${i}-${j}`}
                              data-index={idx}
                              role='gridcell'
                              tabIndex={activeIndex === idx ? 0 : -1}
                              onClick={() => {
                                setActiveIndex(idx);
                                selectDate(cell.date);
                              }}
                              aria-selected={isSelected ? 'true' : undefined}
                            >
                              {cell.date.getDate()}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  <div className='row btn-row mt-16' style={{ justifyContent: 'flex-end' }}>
                    <Button
                      label='Today'
                      className='btn-ghost btn-sm'
                      id={`${fieldId}-today`}
                      onClick={goToday}
                    />
                    <Button
                      label='Clear'
                      className='btn-ghost btn-sm'
                      id={`${fieldId}-clear`}
                      onClick={clear}
                    />
                  </div>
                </div>
              </Card>,
              document.body
            )
          : null}
      </div>
    </div>
  );
}
